package org.teddapp.project.sync

/**
 * État de synchronisation exposé à l'UI (Settings).
 *
 * L'app gère la persistance du flag `enabled`, le compteur de pending,
 * et le déclenchement d'un cycle de sync via le bouton "Synchroniser maintenant".
 */
data class SyncUiState(
    /** L'utilisateur a-t-il activé la sync cloud ? Persisté en AppSettings. */
    val enabled: Boolean,
    /** État courant du moteur. */
    val status: SyncStatus,
    /** Horodatage (epoch ms) de la dernière sync réussie, ou null si jamais. */
    val lastSyncAt: Long?,
    /** Nombre de lignes encore non poussées (synced_at < updated_at). */
    val pendingChanges: Int,
    /** Message d'erreur lisible si le dernier cycle a échoué. */
    val errorMessage: String?,
    /** Ring buffer des N derniers cycles (du plus récent au plus ancien). In-memory. */
    val history: List<SyncRunEntry> = emptyList(),
    /** Une ligne par table : ce qui attend, ce qui est passé, ce que le serveur a. */
    val tables: List<SyncTableStatus> = emptyList(),
    /** Vrai pendant la comparaison avec le serveur. */
    val checkingRemote: Boolean = false,
    /** Instant de la dernière comparaison avec le serveur, ou null. */
    val remoteCheckedAt: Long? = null,
    /** Après un échec, instant avant lequel les cycles automatiques n'insistent pas. Null sinon. */
    val nextRetryAt: Long? = null,
    /** Échecs de cycle d'affilée ; revient à zéro au premier succès. */
    val consecutiveFailures: Int = 0,
)

/**
 * Où en est une table. Les comptes locaux viennent de la base ; le compte distant
 * n'est connu qu'après « vérifier avec le serveur », et vaut null avant.
 */
data class SyncTableStatus(
    val entity: EntityKind,
    /** Lignes locales modifiées depuis leur dernier envoi. */
    val pending: Int,
    /** Lignes locales, supprimées comprises : le distant les porte aussi. */
    val total: Int,
    /** Dernier cycle où cette table est passée sans erreur, ou null. */
    val lastOkAt: Long?,
    /** Dernière erreur sur cette table, ou null si le dernier passage a réussi. */
    val lastError: String?,
    val remoteCount: Long? = null,
) {
    /** Écart entre local et distant, connu seulement après vérification. */
    val gap: Long? get() = remoteCount?.let { total.toLong() - it }
    val healthy: Boolean get() = pending == 0 && lastError == null && (gap == null || gap == 0L)
}

/**
 * Trace d'un cycle de sync passé. Les cinquante dernières sont gardées en préférence
 * locale : l'historique survit au redémarrage.
 */
@kotlinx.serialization.Serializable
data class SyncRunEntry(
    val id: String,
    val startedAt: Long,
    val finishedAt: Long,
    val success: Boolean,
    val trigger: SyncTrigger,
    /** Nombre de lignes envoyées au remote dans ce cycle (depuis le pending compté avant push). */
    val pushedCount: Int,
    /** Message d'erreur si !success. */
    val errorMessage: String? = null,
) {
    val durationMs: Long get() = (finishedAt - startedAt).coerceAtLeast(0L)
}

@kotlinx.serialization.Serializable
enum class SyncTrigger { Manual, Auto, BootCatchup }

sealed interface SyncStatus {
    /** Pas d'opération en cours. */
    data object Idle : SyncStatus

    /** Cycle en cours (push + pull). */
    data object Syncing : SyncStatus

    /** Dernier cycle a échoué — voir `errorMessage` pour le détail. */
    data object Error : SyncStatus
}
